import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { api } from '../api';

interface Genre {
  _id: string;
  key: string;
  title: string;
  subtitle?: string;
  imageUrl?: string;
  order: number;
  active: boolean;
}

interface FormState {
  key: string;
  title: string;
  subtitle: string;
  imageUrl: string;
  order: number;
  active: boolean;
}

const EMPTY: FormState = { key: '', title: '', subtitle: '', imageUrl: '', order: 0, active: true };

export default function Genres() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ items: Genre[] }>({
    queryKey: ['admin', 'genres'],
    queryFn: () => api('/api/v1/admin/genres'),
  });

  const [editing, setEditing] = useState<Genre | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  }

  function openEdit(g: Genre) {
    setEditing(g);
    setForm({
      key: g.key,
      title: g.title,
      subtitle: g.subtitle ?? '',
      imageUrl: g.imageUrl ?? '',
      order: g.order,
      active: g.active,
    });
    setShowForm(true);
  }

  function reset() {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY);
  }

  const upsert = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        // Don't send empty optional strings — undefined keeps the field intact server-side.
        subtitle: form.subtitle.trim() || undefined,
        imageUrl: form.imageUrl.trim() || undefined,
      };
      return editing
        ? api(`/api/v1/admin/genres/${editing._id}`, { method: 'PATCH', body: JSON.stringify(body) })
        : api('/api/v1/admin/genres', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'genres'] });
      reset();
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => api(`/api/v1/admin/genres/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'genres'] }),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    upsert.mutate();
  }

  function confirmDelete(g: Genre) {
    if (confirm(`Delete genre "${g.title}"? This cannot be undone.`)) del.mutate(g._id);
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Genres</h1>
          <p className="text-text2 text-sm mt-1">
            Categories shown on the search screen. Drop matching <span className="text-accent">/covers/genres/&lt;key&gt;.jpg</span> on the CDN to set tile images.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="bg-action-gradient text-white font-semibold px-4 py-2 rounded-lg shadow-glow flex items-center gap-2"
        >
          <Plus size={16} /> New genre
        </button>
      </div>

      {showForm ? (
        <form onSubmit={submit} className="glass p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">
            {editing ? `Edit "${editing.title}"` : 'New genre'}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-text2 mb-1">Key (slug)</label>
              <input
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                placeholder="e.g. afrobeat"
                disabled={!!editing}
                className="w-full bg-[rgba(14,14,16,0.6)] border border-[rgba(120,170,240,0.18)] rounded-md px-3 py-2 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-xs text-text2 mb-1">Display title</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Afrobeat"
                className="w-full bg-[rgba(14,14,16,0.6)] border border-[rgba(120,170,240,0.18)] rounded-md px-3 py-2"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-text2 mb-1">Subtitle (optional)</label>
              <input
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                placeholder="e.g. Sons modernes"
                className="w-full bg-[rgba(14,14,16,0.6)] border border-[rgba(120,170,240,0.18)] rounded-md px-3 py-2"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-text2 mb-1">Image URL</label>
              <input
                value={form.imageUrl}
                onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                placeholder="https://ndulecdn.cyberperformance.ca/covers/genres/<key>.jpg"
                className="w-full bg-[rgba(14,14,16,0.6)] border border-[rgba(120,170,240,0.18)] rounded-md px-3 py-2 font-mono text-xs"
              />
            </div>
            <div>
              <label className="block text-xs text-text2 mb-1">Display order</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                className="w-full bg-[rgba(14,14,16,0.6)] border border-[rgba(120,170,240,0.18)] rounded-md px-3 py-2"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
                <span>Active (visible in app)</span>
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-5">
            <button
              type="submit"
              disabled={upsert.isPending || !form.key || !form.title}
              className="bg-action-gradient text-white px-5 py-2 rounded-lg font-semibold disabled:opacity-50"
            >
              {upsert.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create'}
            </button>
            <button
              type="button"
              onClick={reset}
              className="bg-[rgba(34,64,121,0.6)] text-text px-5 py-2 rounded-lg"
            >
              Cancel
            </button>
            {upsert.isError ? (
              <span className="text-danger text-sm self-center">
                {(upsert.error as Error).message}
              </span>
            ) : null}
          </div>
        </form>
      ) : null}

      <div className="glass overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[rgba(34,64,121,0.4)] text-left text-text2">
            <tr>
              <th className="px-4 py-3 w-16">Image</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Key</th>
              <th className="px-4 py-3">Subtitle</th>
              <th className="px-4 py-3 text-right">Order</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3 text-right w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-textmuted">
                  Loading…
                </td>
              </tr>
            ) : (data?.items ?? []).length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-textmuted">
                  No genres yet. Click "New genre" to add one, or run{' '}
                  <code className="text-accent">seedGenres.js</code> on the backend.
                </td>
              </tr>
            ) : (
              (data?.items ?? []).map((g) => (
                <tr key={g._id} className="border-t border-[rgba(120,170,240,0.18)]">
                  <td className="px-4 py-3">
                    {g.imageUrl ? (
                      <img
                        src={g.imageUrl}
                        alt={g.title}
                        className="w-10 h-10 rounded object-cover border border-[rgba(120,170,240,0.18)]"
                        onError={(e) => ((e.target as HTMLImageElement).style.opacity = '0.2')}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-[rgba(34,64,121,0.4)]" />
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium">{g.title}</td>
                  <td className="px-4 py-3 font-mono text-xs text-accent">{g.key}</td>
                  <td className="px-4 py-3 text-text2">{g.subtitle ?? '—'}</td>
                  <td className="px-4 py-3 text-right">{g.order}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        g.active
                          ? 'bg-success/20 text-success'
                          : 'bg-[rgba(34,64,121,0.6)] text-textmuted'
                      }`}
                    >
                      {g.active ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => openEdit(g)}
                        className="p-2 hover:bg-[rgba(120,170,240,0.15)] rounded transition"
                        title="Edit"
                      >
                        <Pencil size={14} className="text-text2" />
                      </button>
                      <button
                        onClick={() => confirmDelete(g)}
                        className="p-2 hover:bg-danger/20 rounded transition"
                        title="Delete"
                      >
                        <Trash2 size={14} className="text-danger" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
